import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import {
  getInvoiceReportSummary,
  getInvoiceReportDetails,
  exportInvoiceReportDetailsToExcel,
  exportInvoiceReportSummaryToExcel
} from "../../components/Api/GmsBilling";
import { Filter, Download, ChevronDown, ChevronRight } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import {
  hasPagePermissionWithAdmin,
  isUserAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

export default function InvoiceReportPage() {
  const navigate = useNavigate();
  const [summaryData, setSummaryData] = useState([]);
  const [detailsData, setDetailsData] = useState({}); // { date: [invoices] }
  const [expandedRows, setExpandedRows] = useState(new Set()); // Track which rows are expanded
  const [loadingDetails, setLoadingDetails] = useState({}); // Track loading per date
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10); // Fixed, not user-selectable
  const [totalCount, setTotalCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [exportingDate, setExportingDate] = useState(null);

  // Filter options
  const invoiceTypeOptions = [
    { value: "ca", label: "Cash" },
    { value: "cr", label: "Credit" }
  ];

  const paymentMethodOptions = [
    { value: "ca", label: "Cash" },
    { value: "cc", label: "Credit Card" },
    { value: "ba", label: "Bank Transfer" },
    { value: "ta", label: "Tabee" },
    { value: "tm", label: "Tamara" },
    { value: "on", label: "Online" }
  ];

  // Filter state
  const [filter, setFilter] = useState({
    fromDate: new Date(),
    toDate: new Date(),
    invoice_Type: null,
    paymentMethod: null,
  });

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const isAdmin = isUserAdmin();
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsInvoiceReport');

  // Format dates properly to avoid timezone issues
  const formatDateForAPI = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (hasPermission) {
      loadSummaryData();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);

  const loadSummaryData = useCallback(async () => {
    try {
      setLoading(true);

      // Format dates as YYYY-MM-DD strings to avoid timezone issues
      const formattedFromDate = formatDateForAPI(filter.fromDate);
      const formattedToDate = formatDateForAPI(filter.toDate);

      const filterData = {
        fromDate: formattedFromDate,
        toDate: formattedToDate,
        invoice_Type: filter.invoice_Type,
        paymentMethod: filter.paymentMethod
      };

      console.log("📊 Loading invoice report summary with filter:", filterData);

      const response = await getInvoiceReportSummary(filterData);

      console.log("📊 Summary response:", response);

      // Handle both camelCase and PascalCase response structures
      const summaries = response?.summaries || response?.Summaries || [];

      console.log("📊 Parsed summaries:", summaries);

      if (summaries && Array.isArray(summaries)) {
        setSummaryData(summaries);
        setTotalCount(summaries.length);
      } else {
        setSummaryData([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("❌ Error loading summary data:", error);
      Swal.fire("Error", "Failed to load invoice report", "error");
      setSummaryData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const loadDetailsForDate = async (date) => {
    // Handle date - could be Date object or string
    const dateObj = date instanceof Date ? date : new Date(date);
    // Use formatDateForAPI to get YYYY-MM-DD string for the key
    const dateKey = formatDateForAPI(dateObj);

    console.log("🔍 Loading details for date:", {
      originalDate: date,
      dateObj: dateObj,
      dateKey: dateKey,
      formattedForAPI: formatDateForAPI(dateObj)
    });

    // If already loaded, don't reload
    if (detailsData[dateKey]) {
      console.log("✅ Details already loaded for", dateKey);
      return;
    }

    try {
      setLoadingDetails(prev => ({ ...prev, [dateKey]: true }));

      // Format date as YYYY-MM-DD string to avoid timezone issues
      const formattedDate = formatDateForAPI(dateObj);

      const request = {
        date: formattedDate,
        invoice_Type: filter.invoice_Type,
        paymentMethod: filter.paymentMethod
      };

      console.log("📤 Sending details request:", request);

      const response = await getInvoiceReportDetails(request);

      console.log("📥 Details response:", response);

      // Handle both camelCase and PascalCase response structures
      const details = response?.details || response?.Details || [];

      console.log("✅ Parsed details count:", details.length, details);

      setDetailsData(prev => ({
        ...prev,
        [dateKey]: Array.isArray(details) ? details : []
      }));
    } catch (error) {
      console.error("❌ Error loading details for date:", error);
      console.error("❌ Error response:", error.response?.data);
      Swal.fire("Error", `Failed to load invoice details for ${formatDate(dateObj)}`, "error");
      setDetailsData(prev => ({
        ...prev,
        [dateKey]: []
      }));
    } finally {
      setLoadingDetails(prev => ({ ...prev, [dateKey]: false }));
    }
  };

  const handleToggleRow = (date) => {
    // Handle date - could be Date object or string
    const dateObj = date instanceof Date ? date : new Date(date);
    // Use formatDateForAPI to get consistent YYYY-MM-DD string
    const dateKey = formatDateForAPI(dateObj);
    const newExpandedRows = new Set(expandedRows);

    console.log("🔄 Toggling row for date:", {
      originalDate: date,
      dateObj: dateObj,
      dateKey: dateKey
    });

    if (newExpandedRows.has(dateKey)) {
      newExpandedRows.delete(dateKey);
    } else {
      newExpandedRows.add(dateKey);
      // Load details if not already loaded
      if (!detailsData[dateKey]) {
        loadDetailsForDate(dateObj);
      }
    }

    setExpandedRows(newExpandedRows);
  };

  const handleApplyFilter = () => {
    setPage(1);
    setExpandedRows(new Set());
    setDetailsData({});
    loadSummaryData();
  };

  const handleClearFilter = () => {
    const today = new Date();
    setFilter({
      fromDate: today,
      toDate: today,
      invoice_Type: null,
      paymentMethod: null,
    });
    setPage(1);
    setExpandedRows(new Set());
    setDetailsData({});
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleExportDate = async (date) => {
    try {
      setExportingDate(formatDateForAPI(date));

      // Handle date - could be Date object or string
      const dateObj = date instanceof Date ? date : new Date(date);
      // Format date as YYYY-MM-DD string to avoid timezone issues
      const formattedDate = formatDateForAPI(dateObj);

      const request = {
        date: formattedDate,
        invoice_Type: filter.invoice_Type,
        paymentMethod: filter.paymentMethod
      };

      console.log("📤 Exporting details with request:", request);

      await exportInvoiceReportDetailsToExcel(request);
      Swal.fire("Success", "Invoice report exported successfully", "success");
    } catch (error) {
      console.error("❌ Error exporting details:", error);
      Swal.fire("Error", "Failed to export invoice report", "error");
    } finally {
      setExportingDate(null);
    }
  };

  const handleExportSummary = async () => {
    try {
      setLoading(true);

      // Format dates as YYYY-MM-DD strings to avoid timezone issues
      const formattedFromDate = formatDateForAPI(filter.fromDate);
      const formattedToDate = formatDateForAPI(filter.toDate);

      const filterData = {
        fromDate: formattedFromDate,
        toDate: formattedToDate,
        invoice_Type: filter.invoice_Type,
        paymentMethod: filter.paymentMethod
      };

      console.log("📤 Exporting summary with filter:", filterData);

      await exportInvoiceReportSummaryToExcel(filterData);
      Swal.fire("Success", "Invoice report summary exported successfully", "success");
    } catch (error) {
      console.error("❌ Error exporting summary:", error);
      Swal.fire("Error", "Failed to export invoice report summary", "error");
    } finally {
      setLoading(false);
    }
  };

  // Get paginated data for display
  const getPaginatedData = () => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return summaryData.slice(startIndex, endIndex);
  };

  // Calculate grand totals
  const grandTotals = useMemo(() => {
    return summaryData.reduce((acc, row) => ({
      customerCount: acc.customerCount + (row.customerCount || 0),
      grossSales: acc.grossSales + (row.grossSales || 0),
      discount: acc.discount + (row.discount || 0),
      netSales: acc.netSales + (row.netSales || 0),
      cash: acc.cash + (row.cash || 0),
      creditCard: acc.creditCard + (row.creditCard || 0),
      bankTransfer: acc.bankTransfer + (row.bankTransfer || 0),
      tabee: acc.tabee + (row.tabee || 0),
      tamara: acc.tamara + (row.tamara || 0),
      online: acc.online + (row.online || 0),
      credit: acc.credit + (row.credit || 0),
    }), {
      customerCount: 0,
      grossSales: 0,
      discount: 0,
      netSales: 0,
      cash: 0,
      creditCard: 0,
      bankTransfer: 0,
      tabee: 0,
      tamara: 0,
      online: 0,
      credit: 0,
    });
  }, [summaryData]);

  const currencyCode = localStorage.getItem("currencyCode") || "AED";

  // Helper to format currency
  const formatCurrency = (amount) => {
    return `${currencyCode} ${new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0)}`;
  };

  // Format date
  const formatDate = (dateString) => {
    try {
      const date = dateString instanceof Date ? dateString : new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return "N/A";
    }
  };

  if (!hasPermission) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center">
            <h4>Access Denied</h4>
            <p>You don't have permission to access this page.</p>
          </div>
        </Container>
      </div>
    );
  }

  const paginatedData = getPaginatedData();

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Invoice Report" breadcrumbItem="Reports" />

        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="card-title mb-0">
                    Invoice Report
                  </h4>
                  <div>
                    <Button
                      color="primary"
                      className="me-2"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      <Filter className="me-1" size={16} />
                      {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </Button>
                    <Button
                      color="success"
                      onClick={handleExportSummary}
                      disabled={loading || summaryData.length === 0}
                    >
                      <Download className="me-1" size={16} />
                      Export Summary
                    </Button>
                  </div>
                </div>

                {/* Filter Section */}
                {showFilters && (
                  <Card className="mb-3">
                    <CardBody>
                      <Row>
                        {/* Commented out Invoice Number filter */}
                        {/* <Col md={3}>
                          <div className="mb-3">
                            <Label for="filter_invoice_Id">Invoice Number</Label>
                            <Input
                              id="filter_invoice_Id"
                              type="text"
                              value={filter.invoice_Id || ""}
                              onChange={(e) =>
                                setFilter({ ...filter, invoice_Id: e.target.value || null })
                              }
                              placeholder="Search invoice number..."
                            />
                          </div>
                        </Col> */}

                        {/* Commented out Customer filter */}
                        {/* <Col md={3}>
                          <div className="mb-3">
                            <Label for="filter_customer_Id">Customer Name</Label>
                            <Select
                              id="filter_customer_Id"
                              value={selectedCustomerFilterOption}
                              onChange={(option) => {
                                setSelectedCustomerFilterOption(option);
                                setFilter({
                                  ...filter,
                                  customer_Id: option ? option.value : null,
                                });
                              }}
                              options={customerSearchOptions}
                              isClearable
                              isSearchable
                              placeholder="Type at least 3 characters to search customers..."
                              styles={selectStyles}
                              isLoading={customerSearchLoading}
                              noOptionsMessage={() => "Type at least 3 characters to search"}
                              loadingMessage={() => "Searching customers..."}
                              onInputChange={(inputValue) => {
                                if (searchTimeout) {
                                  clearTimeout(searchTimeout);
                                }
                                const timeout = setTimeout(() => {
                                  searchCustomersManually(inputValue);
                                }, 300);
                                setSearchTimeout(timeout);
                                return inputValue;
                              }}
                            />
                          </div>
                        </Col> */}

                        <Col md={3}>
                          <div className="mb-3">
                            <Label for="filter_fromDate">From Date</Label>
                            <DatePicker
                              selected={filter.fromDate}
                              onChange={(date) => setFilter({ ...filter, fromDate: date })}
                              className="form-control"
                              placeholderText="Select from date"
                              dateFormat="dd/MM/yyyy"
                              maxDate={filter.toDate}
                            />
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="mb-3">
                            <Label for="filter_toDate">To Date</Label>
                            <DatePicker
                              selected={filter.toDate}
                              onChange={(date) => setFilter({ ...filter, toDate: date })}
                              className="form-control"
                              placeholderText="Select to date"
                              dateFormat="dd/MM/yyyy"
                              minDate={filter.fromDate}
                            />
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={3}>
                          <div className="mb-3">
                            <Label for="filter_invoice_Type">Invoice Type</Label>
                            <Select
                              id="filter_invoice_Type"
                              value={
                                filter.invoice_Type
                                  ? invoiceTypeOptions.find(option => option.value === filter.invoice_Type)
                                  : null
                              }
                              onChange={(option) =>
                                setFilter({
                                  ...filter,
                                  invoice_Type: option ? option.value : null,
                                })
                              }
                              options={invoiceTypeOptions}
                              isClearable
                              placeholder="Select Invoice Type"
                              styles={selectStyles}
                            />
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="mb-3">
                            <Label for="filter_paymentMethod">Payment Method</Label>
                            <Select
                              id="filter_paymentMethod"
                              value={
                                filter.paymentMethod
                                  ? paymentMethodOptions.find(option => option.value === filter.paymentMethod)
                                  : null
                              }
                              onChange={(option) =>
                                setFilter({
                                  ...filter,
                                  paymentMethod: option ? option.value : null,
                                })
                              }
                              options={paymentMethodOptions}
                              isClearable
                              placeholder="Select Payment Method"
                              styles={selectStyles}
                            />
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-3 d-flex gap-2" style={{ marginTop: "32px" }}>
                            <Button
                              color="primary"
                              size="sm"
                              onClick={handleApplyFilter}
                              disabled={loading}
                            >
                              Apply Filter
                            </Button>
                            <Button
                              color="secondary"
                              size="sm"
                              onClick={handleClearFilter}
                              disabled={loading}
                            >
                              Clear
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                )}

                {/* Table Section */}
                <Card>
                  <CardBody>
                    <h5 className="card-title mb-3">
                      Invoice Report ({totalCount} records)
                    </h5>
                    <div className="table-responsive">
                      {loading ? (
                        <div className="text-center py-4">
                          <ClipLoader color="#556ee6" loading={loading} size={35} />
                          <p className="mt-2">Loading invoice report...</p>
                        </div>
                      ) : (
                        <>
                          <Table bordered>
                            <thead className="table-light">
                              <tr>
                                <th style={{ width: '40px' }}></th>
                                <th>Bill Date</th>
                                <th>Customer Count</th>
                                <th>Gross Sales</th>
                                <th>Discount</th>
                                <th>Net Sales</th>
                                <th>Cash</th>
                                <th>Credit Card</th>
                                <th>Bank Transfer</th>
                                <th>Tabee</th>
                                <th>Tamara</th>
                                <th>Online</th>
                                <th>Credit</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paginatedData.length > 0 ? (
                                paginatedData.map((row, index) => {
                                  // Handle date from API - could be string or Date object
                                  const rowDate = row.billDate instanceof Date ? row.billDate : new Date(row.billDate);
                                  const dateKey = formatDateForAPI(rowDate);
                                  const isExpanded = expandedRows.has(dateKey);
                                  const dateDetails = detailsData[dateKey] || [];
                                  const isLoadingDetails = loadingDetails[dateKey];
                                  const isExporting = exportingDate === dateKey;

                                  return (
                                    <React.Fragment key={dateKey}>
                                      <tr>
                                        <td>
                                          <Button
                                            color="link"
                                            size="sm"
                                            onClick={() => handleToggleRow(rowDate)}
                                            className="p-0"
                                          >
                                            {isExpanded ? (
                                              <ChevronDown size={16} />
                                            ) : (
                                              <ChevronRight size={16} />
                                            )}
                                          </Button>
                                        </td>
                                        <td>{formatDate(rowDate)}</td>
                                        <td>{row.customerCount || 0}</td>
                                        <td>{formatCurrency(row.grossSales)}</td>
                                        <td>{formatCurrency(row.discount)}</td>
                                        <td><strong>{formatCurrency(row.netSales)}</strong></td>
                                        <td>{formatCurrency(row.cash)}</td>
                                        <td>{formatCurrency(row.creditCard)}</td>
                                        <td>{formatCurrency(row.bankTransfer)}</td>
                                        <td>{formatCurrency(row.tabee)}</td>
                                        <td>{formatCurrency(row.tamara || 0)}</td>
                                        <td>{formatCurrency(row.online || 0)}</td>
                                        <td>{formatCurrency(row.credit)}</td>
                                        <td>
                                          <Button
                                            color="success"
                                            size="sm"
                                            onClick={() => handleExportDate(rowDate)}
                                            disabled={isExporting || loading}
                                            style={{ minWidth: '80px' }}
                                          >
                                            {isExporting ? (
                                              <ClipLoader size={14} color="#fff" />
                                            ) : (
                                              <>
                                                <Download size={14} className="me-1" />
                                                Export
                                              </>
                                            )}
                                          </Button>
                                        </td>
                                      </tr>
                                      {isExpanded && (
                                        <tr>
                                          <td colSpan="14" className="p-0">
                                            <div className="p-3" style={{ backgroundColor: '#f8f9fa' }}>
                                              {isLoadingDetails ? (
                                                <div className="text-center py-3">
                                                  <ClipLoader color="#556ee6" size={20} />
                                                  <p className="mt-2 mb-0">Loading invoice details...</p>
                                                </div>
                                              ) : dateDetails.length > 0 ? (
                                                <div>
                                                  <h6 className="mb-3">Invoice Details for {formatDate(rowDate)}</h6>
                                                  <Table size="sm" className="mb-0">
                                                    <thead>
                                                      <tr>
                                                        <th>Invoice No</th>
                                                        <th>Customer</th>
                                                        <th>Invoice Type</th>
                                                        <th>Payment Method</th>
                                                        <th>Gross Sales</th>
                                                        <th>Discount</th>
                                                        <th>Net Sales</th>
                                                        <th>Cash</th>
                                                        <th>Credit Card</th>
                                                        <th>Bank Transfer</th>
                                                        <th>Tabee</th>
                                                        <th>Tamara</th>
                                                        <th>Credit</th>
                                                      </tr>
                                                    </thead>
                                                    <tbody>
                                                      {dateDetails.map((detail, idx) => (
                                                        <tr key={idx}>
                                                          <td>{(detail.isReturn ? '' : 'INV-') + detail.invoice_No}</td>
                                                          <td>{detail.customerName || 'N/A'}</td>
                                                          <td>
                                                            <span className={`badge ${detail.invoice_Type === 'ca' ? 'bg-success' : 'bg-warning'}`}>
                                                              {detail.invoice_Type === 'ca' ? 'Cash' : 'Credit'}
                                                            </span>
                                                          </td>
                                                          <td>
                                                            {detail.paymentMethod === 'ca' ? 'Cash' :
                                                              detail.paymentMethod === 'cc' ? 'Credit Card' :
                                                                detail.paymentMethod === 'ba' ? 'Bank Transfer' :
                                                                  detail.paymentMethod === 'ta' ? 'Tabee' :
                                                                    detail.paymentMethod === 'tm' ? 'Tamara' :
                                                                      detail.paymentMethod === 'on' ? 'Online' : 'N/A'}
                                                          </td>
                                                          <td>{formatCurrency(detail.grossSales)}</td>
                                                          <td>{formatCurrency(detail.discount)}</td>
                                                          <td style={{ color: detail.isReturn ? '#dc3545' : 'inherit' }}>
                                                            {formatCurrency(detail.netSales)}
                                                          </td>
                                                          <td style={{ color: detail.isReturn && detail.cashAmount > 0 ? '#dc3545' : 'inherit' }}>
                                                            {formatCurrency(detail.cashAmount)}
                                                          </td>
                                                          <td style={{ color: detail.isReturn && detail.creditCardAmount > 0 ? '#dc3545' : 'inherit' }}>
                                                            {formatCurrency(detail.creditCardAmount)}
                                                          </td>
                                                          <td style={{ color: detail.isReturn && detail.bankTransferAmount > 0 ? '#dc3545' : 'inherit' }}>
                                                            {formatCurrency(detail.bankTransferAmount)}
                                                          </td>
                                                          <td style={{ color: detail.isReturn && detail.tabeeAmount > 0 ? '#dc3545' : 'inherit' }}>
                                                            {formatCurrency(detail.tabeeAmount)}
                                                          </td>
                                                          <td style={{ color: detail.isReturn && detail.tamaraAmount > 0 ? '#dc3545' : 'inherit' }}>
                                                            {formatCurrency(detail.tamaraAmount || 0)}
                                                          </td>
                                                          <td style={{ color: detail.isReturn && detail.creditAmount > 0 ? '#dc3545' : 'inherit' }}>
                                                            {formatCurrency(detail.creditAmount)}
                                                          </td>
                                                        </tr>
                                                      ))}
                                                    </tbody>
                                                  </Table>
                                                </div>
                                              ) : (
                                                <div className="text-center py-3">
                                                  <p className="mb-0 text-muted">No invoice details found for this date</p>
                                                </div>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan="13" className="text-center">No data found</td>
                                </tr>
                              )}
                            </tbody>
                            {summaryData.length > 0 && (
                              <tfoot>
                                <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                                  <td colSpan="2"><strong>Grand Total</strong></td>
                                  <td>{grandTotals.customerCount}</td>
                                  <td>{formatCurrency(grandTotals.grossSales)}</td>
                                  <td>{formatCurrency(grandTotals.discount)}</td>
                                  <td><strong>{formatCurrency(grandTotals.netSales)}</strong></td>
                                  <td>{formatCurrency(grandTotals.cash)}</td>
                                  <td>{formatCurrency(grandTotals.creditCard)}</td>
                                  <td>{formatCurrency(grandTotals.bankTransfer)}</td>
                                  <td>{formatCurrency(grandTotals.tabee)}</td>
                                  <td>{formatCurrency(grandTotals.tamara)}</td>
                                  <td>{formatCurrency(grandTotals.online)}</td>
                                  <td>{formatCurrency(grandTotals.credit)}</td>
                                  <td></td>
                                </tr>
                              </tfoot>
                            )}
                          </Table>

                          {/* Pagination */}
                          <div className="d-flex justify-content-between align-items-center mt-4 p-3"
                            style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6' }}>
                            <div className="text-muted" style={{ fontSize: '14px', fontWeight: '500' }}>
                              <i className="mdi mdi-information-outline me-2" style={{ color: '#556ee6' }}></i>
                              Showing <span style={{ color: '#556ee6', fontWeight: '600' }}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{ color: '#556ee6', fontWeight: '600' }}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{ color: '#556ee6', fontWeight: '600' }}>{totalCount}</span> entries
                            </div>
                            {totalCount > pageSize && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Button
                                  color="light"
                                  size="sm"
                                  disabled={page === 1}
                                  onClick={() => handlePageChange(page - 1)}
                                  style={{
                                    border: '1px solid #dee2e6',
                                    borderRadius: '0.375rem',
                                    minWidth: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                                    color: page === 1 ? '#6c757d' : '#495057'
                                  }}
                                >
                                  <i className="mdi mdi-chevron-left"></i>
                                </Button>

                                {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                                  const pageNum = index + 1;
                                  const isActive = page === pageNum;

                                  return (
                                    <Button
                                      key={pageNum}
                                      color={isActive ? "primary" : "light"}
                                      size="sm"
                                      onClick={() => handlePageChange(pageNum)}
                                      style={{
                                        border: '1px solid #dee2e6',
                                        borderRadius: '0.375rem',
                                        minWidth: '32px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: isActive ? '#556ee6' : '#fff',
                                        borderColor: isActive ? '#556ee6' : '#dee2e6',
                                        color: isActive ? '#fff' : '#495057',
                                        fontWeight: isActive ? '600' : '400',
                                        boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                                      }}
                                    >
                                      {pageNum}
                                    </Button>
                                  );
                                })}

                                <Button
                                  color="light"
                                  size="sm"
                                  disabled={page === Math.ceil(totalCount / pageSize)}
                                  onClick={() => handlePageChange(page + 1)}
                                  style={{
                                    border: '1px solid #dee2e6',
                                    borderRadius: '0.375rem',
                                    minWidth: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: page === Math.ceil(totalCount / pageSize) ? '#f8f9fa' : '#fff',
                                    color: page === Math.ceil(totalCount / pageSize) ? '#6c757d' : '#495057'
                                  }}
                                >
                                  <i className="mdi mdi-chevron-right"></i>
                                </Button>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
